import React from 'react';

import type {
  ComponentRenderProps,
  HeadingProps,
} from '../../../types/dynamic_views';
import { useManifestId } from '../ManifestContext';
import { renderTextWithIcons } from '../utils/emojiToIcon';

export const HeadingComponent: React.FC<ComponentRenderProps> = ({
  component,
  className = '',
}) => {
  const props = component.props as HeadingProps;
  const {
    text,
    level = 1,
    color = 'neutral',
    align = 'left',
    size = 'md',
  } = props;
  const manifestId = useManifestId();

  // Generate appropriate heading tag
  const HeadingTag = `h${Math.min(Math.max(level, 1), 6)}` as
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6';

  // Build CSS classes - using design system tokens for dark mode support
  const colorClasses = {
    primary: 'text-fg-primary',
    secondary: 'text-fg-tertiary',
    accent: 'text-accent',
    neutral: 'text-fg-primary',
  };

  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  // Reduced heading sizes for cleaner appearance
  const mdSizeByLevel: Record<number, string> = {
    1: 'text-xl',
    2: 'text-lg',
    3: 'text-base',
  };
  const lgSizeByLevel: Record<number, string> = {
    1: 'text-2xl',
    2: 'text-xl',
    3: 'text-lg',
  };
  const xlSizeByLevel: Record<number, string> = {
    1: 'text-3xl',
    2: 'text-2xl',
    3: 'text-xl',
  };

  const sizeClasses = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: mdSizeByLevel[level] ?? 'text-sm',
    lg: lgSizeByLevel[level] ?? 'text-base',
    xl: xlSizeByLevel[level] ?? 'text-lg',
  };

  // Weight based on heading level
  let weightClasses = 'font-medium';
  if (level <= 2) {
    weightClasses = 'font-bold';
  } else if (level <= 4) {
    weightClasses = 'font-semibold';
  }

  // Spacing: more padding around headings, but first:mt-0 removes top margin on first element
  let spacingClasses = 'mt-4 mb-1.5 first:mt-0';
  if (level === 1) {
    spacingClasses = 'mt-6 mb-3 first:mt-0';
  } else if (level === 2) {
    spacingClasses = 'mt-5 mb-2 first:mt-0';
  }

  const allClasses = [
    colorClasses[color] || colorClasses.neutral,
    alignClasses[align] || alignClasses.left,
    sizeClasses[size] || sizeClasses.md,
    weightClasses,
    spacingClasses,
    'leading-tight',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Scale icon size based on heading level (reduced to match smaller headings)
  let iconSize = 'w-3.5 h-3.5';
  if (level === 1) {
    iconSize = 'w-5 h-5';
  } else if (level <= 3) {
    iconSize = 'w-4 h-4';
  }
  const iconClassName = `${iconSize} inline-block relative -top-[2px] mr-1`;

  // Unlike ListComponent, this does not extract text/content/label from
  // object-shaped input — heading text is never expected to be an object,
  // so it's guarded-and-blanked rather than recovered. Only `list` items
  // get object-shape tolerance, per the production incident that motivated it.
  if (typeof text !== 'string') {
    console.warn('HeadingComponent: text is not a string', typeof text);
  }
  const safeText = typeof text === 'string' ? text : '';

  return (
    <HeadingTag
      className={allClasses}
      data-component-id={component.id}
      data-level={level}
    >
      {renderTextWithIcons(safeText, iconClassName, manifestId)}
    </HeadingTag>
  );
};

export default HeadingComponent;
